// src/types/Product.ts
export interface Product {
  id?: string;
  name: string;
  description: string;
  price: number;
  discount?: number; // Discount percentage (0-100)
  unitCost?: number;
  isOutOfStock: boolean;
  categoryId: string;
  subcategoryId?: string;
  brandId?: string; // Optional brand association
  sku: string;
  images: string[];
  variants: ProductVariant[];
  hasVariants: boolean;
  // Free-text search/discovery tags (e.g. "wedding", "gift", "summer") —
  // matched by both admin and client search, and by the client's tag filter.
  tags?: string[];
  // Featured product flag
  isFeatured: boolean;
  // Stock management fields (total across all warehouses)
  stockQuantity: number;
  minStockLevel: number;
  maxStockLevel: number;
  isLowStock: boolean;
  // Warehouse allocations for simple products (stored in product document)
  warehouseAllocations?: WarehouseAllocation[];
  // When true, stock for this product is tracked as individual serial-numbered
  // units (availableSerials on WarehouseInventory) instead of a plain count.
  requiresSerialTracking?: boolean;
  createdAt?: Date;
  updatedAt?: Date;
  createdBy?: string;
}

export interface ProductFormData {
  name: string;
  description: string;
  price: string;
  discount: string; // Discount percentage as string for form input
  unitCost: string;
  isOutOfStock: boolean;
  isFeatured?: boolean;
  categoryId: string;
  subcategoryId: string;
  brandId: string; // Optional brand association (empty string = none)
  sku: string;
  images: File[];
  variants: ProductVariant[];
  hasVariants: boolean;
  tags: string[];
  // Stock management fields
  stockQuantity: string;
  minStockLevel: string;
  maxStockLevel: string;
  // Warehouse allocations (for UI only, not stored in product)
  warehouseAllocations: WarehouseAllocation[];
  requiresSerialTracking?: boolean;
}

// Warehouse Allocation Interface (for form data)
export interface WarehouseAllocation {
  warehouseId: string;
  warehouseName?: string;
  quantity: number;
  minStockLevel: number;
  maxStockLevel: number;
  isLowStock: boolean;
  isOutOfStock: boolean;
  isOverStock: boolean;
  // Present only when the parent product/variant has requiresSerialTracking
  // set. quantity is always kept equal to serialNumbers.length in that case.
  serialNumbers?: string[];
}

// Warehouse Inventory Record (stored separately)
export interface WarehouseInventory {
  id?: string;
  productId: string;
  productName?: string;
  variantId?: string;
  variantName?: string;
  quantity: number;
  minStockLevel: number;
  maxStockLevel: number;
  isLowStock: boolean;
  isOutOfStock: boolean;
  isOverStock: boolean;
  lastUpdated?: Date;
  sku?: string;
  price?: number;
  discount?: number; // Discount percentage
  // Weighted-average purchase cost for stock currently in this warehouse,
  // recomputed on every PO receipt. Always cost — never conflated with the
  // selling price above (see answers.text for the historical ambiguity here).
  avgCost?: number;
  // Serial-tracked stock: when present, this array *is* the stock — quantity
  // is always kept equal to availableSerials.length. Absent for ordinary
  // (non-serialized) products/variants, which behave exactly as before.
  availableSerials?: string[];
}

// Category Management Types
export interface Category {
  id?: string;
  name: string;
  description?: string;
  parentId?: string;
  imageUrl?: string | null;
  isActive: boolean;
  sortOrder?: number;
  createdAt?: Date;
  updatedAt?: Date;
  createdBy?: string;
  isStarred?: boolean;
}

export interface CategoryFormData {
  name: string;
  description: string;
  parentId: string;
  isActive: boolean;
  sortOrder: string;
}

// Variant Management Types
export interface VariantOption {
  id?: string;
  name: string;
  value?: string;
  isActive: boolean;
}

export interface VariantType {
  id?: string;
  name: string;
  isRequired: boolean;
  allowsMultiple: boolean;
  options: VariantOption[];
  isActive: boolean;
  createdAt?: Date;
  updatedAt?: Date;
  createdBy?: string;
}

export interface VariantSelection {
  variantType: string;
  variantValue: string;
}

export interface ProductVariant {
  id?: string;
  productId?: string;
  variantSelections: VariantSelection[];
  price: number;
  discount?: number; // Discount percentage (0-100)
  unitCost?: number;
  sku: string;
  isDefault?: boolean;
  // Stock management for variants (total across all warehouses)
  stockQuantity: number;
  minStockLevel: number;
  maxStockLevel: number;
  isLowStock: boolean;
  isOutOfStock: boolean;
  isOverStock: boolean;
  // Warehouse allocations (for UI only)
  warehouseAllocations?: WarehouseAllocation[];
  requiresSerialTracking?: boolean;
}

export interface VariantTypeFormData {
  name: string;
  isActive: boolean;
  options: VariantOption[];
}

// Utility interfaces
export interface CategoryWithSubcategories extends Category {
  subcategories: Category[];
}

export interface ProductWithDetails extends Product {
  category?: Category;
  subcategory?: Category;
  variantDetails?: {
    variantSelections: VariantSelection[];
    price: number;
    discount?: number; // Discount percentage
    sku: string;
    isDefault?: boolean;
    stockQuantity: number;
    minStockLevel: number;
    maxStockLevel: number;
    warehouseAllocations?: WarehouseAllocation[];
    isLowStock: boolean;
    isOutOfStock: boolean;
  }[];
  warehouseInventories?: WarehouseInventory[];
}

// Global configuration for variants
export const VARIANT_CONFIG = {
  maxVariantSelections: 2,
} as const;

// Import Warehouse types
export interface Warehouse {
  id?: string;
  name: string;
  code: string;
  description: string;
  address: WarehouseAddress;
  contact: WarehouseContact;
  capacity: WarehouseCapacity;
  isActive: boolean;
  // When true, this warehouse is surfaced as a public "branch" on the client website.
  displayInWebsite?: boolean;
  operatingHours?: WarehouseOperatingHours;
  createdAt?: Date;
  updatedAt?: Date;
  createdBy?: string;
  createdByName?: string;
  updatedBy?: string;
  updatedByName?: string;
  // Statistics that will be updated
  stats?: WarehouseStats;
}

export interface WarehouseAddress {
  street: string;
  city: string;
  state: string;
  country: string;
  zipCode: string;
}

export interface WarehouseContact {
  phone: string;
  email: string;
  manager: string;
}

export interface WarehouseCapacity {
  total: number;
  used: number;
  available: number;
}

export type WeekDay =
  | 'monday'
  | 'tuesday'
  | 'wednesday'
  | 'thursday'
  | 'friday'
  | 'saturday'
  | 'sunday';

export const WEEK_DAYS: WeekDay[] = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

export interface DayHours {
  isOpen: boolean;
  open: string;
  close: string;
}

export interface WarehouseOperatingHours {
  // Kept for backward compatibility — treated as the default hours for any
  // day that does not have an explicit entry in `days`.
  open: string;
  close: string;
  timezone: string;
  // Per-day opening hours. Optional so legacy warehouse docs keep working.
  days?: Record<WeekDay, DayHours>;
}

// Sensible default weekly schedule (Mon–Fri open, weekend closed).
export const createDefaultWeekHours = (
  open = '09:00',
  close = '18:00'
): Record<WeekDay, DayHours> => ({
  monday: { isOpen: true, open, close },
  tuesday: { isOpen: true, open, close },
  wednesday: { isOpen: true, open, close },
  thursday: { isOpen: true, open, close },
  friday: { isOpen: true, open, close },
  saturday: { isOpen: false, open, close },
  sunday: { isOpen: false, open, close },
});

export interface WarehouseStats {
  productCount: number;
  variantCount: number;
  totalStock: number;
  lowStockCount: number;
  outOfStockCount: number;
}

export interface WarehouseFormData {
  name: string;
  code: string;
  description: string;
  address: WarehouseAddress;
  contact: WarehouseContact;
  capacity: {
    total: string;
    used: string;
  };
  isActive: boolean;
  displayInWebsite: boolean;
  operatingHours: WarehouseOperatingHours;
}

export interface WarehouseDashboardStats {
  total: number;
  active: number;
  inactive: number;
  totalCapacity: number;
  usedCapacity: number;
  availableCapacity: number;
  utilizationRate: number;
}

// Warehouse with inventory
export interface WarehouseWithInventory extends Warehouse {
  inventory: WarehouseInventory[];
}

// Utility function to calculate discounted price
export const calculateDiscountedPrice = (price: number, discount?: number): number => {
  if (!discount || discount <= 0 || discount > 100) {
    return price;
  }
  return price - (price * discount / 100);
};

// Utility function to calculate discount amount
export const calculateDiscountAmount = (price: number, discount?: number): number => {
  if (!discount || discount <= 0 || discount > 100) {
    return 0;
  }
  return price * discount / 100;
};